import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Observable } from 'rxjs';
import { ModalComponent } from '../modal.component';
import { MODAL_CONFIG, MODAL_DATA, MODAL_REF, ModalConfig } from '../modal.service';
import { ButtonComponent } from '../../forms/button/button.component';
import { CheckboxComponent } from '../../forms/checkbox/checkbox.component';
import { TitleIfTruncatedDirective } from '../../cabinet/card/title-if-truncated.directive';

export type ConfirmDialogTone = 'default' | 'danger';
export type ConfirmDialogResult = 'confirm' | 'secondary' | 'cancel';

export interface ConfirmDialogOptions {
  /** Verb + object: "Delete Notification Recipient". */
  title: string;
  /** Names the item the dialog acts on, shown under the title. */
  subject?: string;
  /** The question, in bold: "Delete this recipient?" */
  question?: string;
  /** Plain explanation under the question. */
  message?: string;
  /** Verb + object, never "OK" or "Yes": "Delete Recipient". */
  confirmLabel: string;
  cancelLabel?: string;
  /** Optional second choice, shown as an outline button. */
  secondaryLabel?: string;
  /** `danger`: red confirm button and focus starts on Cancel. */
  tone?: ConfirmDialogTone;
  /** A required checkbox, e.g. "I understand this can't be undone". */
  acknowledge?: string;
  /**
   * Work to run on confirm. The dialog stays open and busy until it
   * completes; on error it shows `errorMessage` and lets the user retry.
   */
  action?: () => Observable<unknown>;
  /** Fixed text, or built from the action's error (e.g. the server message). */
  errorMessage?: string | ((error: unknown) => string | null | undefined);
}

let nextId = 0;

/** Content of a dialog opened by ConfirmDialogService. */
@Component({
  selector: 'app-confirm-dialog',
  templateUrl: './confirm-dialog.component.html',
  styleUrl: './confirm-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, ModalComponent, ButtonComponent, CheckboxComponent, TitleIfTruncatedDirective],
})
export class ConfirmDialogComponent {
  private readonly modalRef = inject(MODAL_REF);
  private readonly config = inject<ModalConfig>(MODAL_CONFIG);
  private readonly destroyRef = inject(DestroyRef);
  readonly options = inject(MODAL_DATA) as ConfirmDialogOptions;

  readonly danger = this.options.tone === 'danger';
  readonly questionId = `confirm-question-${nextId}`;
  readonly messageId = `confirm-message-${nextId++}`;
  readonly describedBy = [this.options.question ? this.questionId : null, this.options.message ? this.messageId : null]
    .filter((id): id is string => id !== null)
    .join(' ') || null;
  readonly cancelLabel = this.options.cancelLabel ?? 'Cancel';

  readonly acknowledged = signal(false);
  readonly busy = signal(false);
  readonly error = signal<string | null>(null);

  readonly confirmDisabled = computed(() => this.busy() || (!!this.options.acknowledge && !this.acknowledged()));

  confirm(): void {
    if (this.confirmDisabled()) return;
    const action = this.options.action;
    if (!action) {
      this.modalRef.close('confirm');
      return;
    }
    this.setBusy(true);
    this.error.set(null);
    action()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => undefined,
        complete: () => {
          this.setBusy(false);
          this.modalRef.close('confirm');
        },
        error: (err: unknown) => {
          this.setBusy(false);
          const errorMessage = this.options.errorMessage;
          const text = typeof errorMessage === 'function' ? errorMessage(err) : errorMessage;
          this.error.set(text || 'Something went wrong. Please try again.');
        },
      });
  }

  secondary(): void {
    if (!this.busy()) this.modalRef.close('secondary');
  }

  cancel(): void {
    if (!this.busy()) this.modalRef.close('cancel');
  }

  private setBusy(busy: boolean): void {
    this.busy.set(busy);
    this.config.disableClose = busy;
  }
}
